/**
 * MavenPromo Sample Data
 * E-commerce promotional products platform with customization features
 */

import type { Project, Task, Epic, Sprint, Priority, TaskStatus, StandupNote } from '../types';

const ORG_ID = 'org_intellesphere';
const PROJECT_ID = 'proj-mavenpromo';

export const mavenPromoProject: Project = {
  id: PROJECT_ID,
  organizationId: ORG_ID,
  organizationIds: ['org_intellesphere', 'org_intellespheredimentry'], // Multi-org support: visible in both Intellesphere and Dimentry
  name: 'MavenPromo',
  description: 'E-commerce platform for promotional products with 2D/3D customization studio, product management, and order processing',
  status: 'In Progress',
  proposal: `# MavenPromo - Promotional Products Platform

## Overview
Build a comprehensive e-commerce platform for promotional products featuring advanced customization capabilities, robust product management, and seamless order processing.

## Key Objectives
- 2D/3D Product Customization Studio
- Multi-category Product Management
- Order Management & Tracking
- Admin Panel with Configuration Tools
- Payment Integration (Stripe, Authorize.net)
- Email Notifications & Order Confirmation
- Firebase Infrastructure
- Production Order Management

## Technology Stack
- Frontend: React/Next.js
- Backend: Firebase (Firestore, Cloud Functions, Storage, Auth)
- Customization: Three.js for 3D, Canvas API for 2D
- Payment: Stripe, Authorize.net
- Deployment: Firebase App Hosting with GitHub Actions
- Email: Resend
- Image Hosting: Cloudinary/Firebase Storage

## Current Status
The platform is in active development with core features implemented including:
- Complete checkout workflow
- Product catalog with categories and subcategories
- Admin panel for product and content management
- 2D customization studio (in progress)
- Order tracking and management
- Payment processing integration`,
  costs: [
    { id: 'cost-mp-1', description: 'Firebase Services (Annual)', amount: 24000, category: 'Infrastructure' },
    { id: 'cost-mp-2', description: 'Development Team (12 months)', amount: 960000, category: 'Personnel' },
    { id: 'cost-mp-3', description: 'Payment Gateway Fees', amount: 12000, category: 'Services' },
    { id: 'cost-mp-4', description: 'Design & UX', amount: 48000, category: 'Design' },
    { id: 'cost-mp-5', description: 'Third-party APIs (Cloudinary, etc)', amount: 6000, category: 'Services' },
  ],
  teamMembers: [
    { userId: 'hossainzarif2002@gmail.com', role: 'developer' },
    { userId: 'aidimentry@gmail.com', role: 'developer' },
    { userId: 'user-emtiaj', role: 'developer' },
    { userId: 'user-jakaria', role: 'developer' },
    { userId: 'user-sakib', role: 'developer' },
    { userId: 'user-adil', role: 'developer' },
    { userId: 'pemsapi', role: 'stakeholder' },
    { userId: 'user-khaled', role: 'stakeholder' },
    { userId: 'user-tias', role: 'developer' },
  ],
  createdAt: '2025-08-01T00:00:00Z',
  updatedAt: '2026-02-22T00:00:00Z',
  externalLinks: [
    { type: 'Production', url: 'https://mavenpromo.com' },
    { type: 'Admin', url: 'https://admin.mavenpromo.com' },
  ],
  startDate: '2025-08-01T00:00:00Z',
  targetEndDate: '2026-12-31T00:00:00Z',
};

// Epic definitions based on the CSV categories
export const mavenPromoEpics: Epic[] = [
  {
    id: 'epic-mp-checkout',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'U_Checkout - Checkout & Cart',
    description: 'Complete checkout workflow from add to cart to order confirmation including payment processing',
    status: 'Completed',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-08-19T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
  },
  {
    id: 'epic-mp-admin',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'U_Admin - Admin Panel',
    description: 'Admin utilities for category, clipart, product management, user management and site configuration',
    status: 'Completed',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-08-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
  },
  {
    id: 'epic-mp-customize',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'U_Customize - Customization Studio',
    description: '2D/3D customization studio with text, clipart, safe areas, layer management, and export functionality',
    status: 'In Progress',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-08-01T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
  },
  {
    id: 'epic-mp-product',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'U_Product - Product Management',
    description: 'Product catalog, detail pages, search, filtering, and pricing engine',
    status: 'Completed',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-08-10T00:00:00Z',
    updatedAt: '2026-01-20T00:00:00Z',
  },
  {
    id: 'epic-mp-order-tracking',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'U_Order_Tracking - Order Management',
    description: 'Order tracking, status updates, admin order management, and customer order history',
    status: 'Completed',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-09-01T00:00:00Z',
    updatedAt: '2026-01-30T00:00:00Z',
  },
  {
    id: 'epic-mp-infrastructure',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'U_Infrastructure - Firebase & Deployment',
    description: 'Firebase setup, authentication, Firestore, cloud functions, storage, and deployment pipeline',
    status: 'Completed',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-08-01T00:00:00Z',
    updatedAt: '2025-10-01T00:00:00Z',
  },
  {
    id: 'epic-mp-browse',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'U_Browse - Navigation & Discovery',
    description: 'Homepage, category navigation, search functionality, and product discovery features',
    status: 'In Progress',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-09-10T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
  },
  {
    id: 'epic-mp-payment',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'U_Payment - Payment Processing',
    description: 'Stripe and Authorize.net integration, payment orchestration, and discounts/coupons',
    status: 'Completed',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-08-25T00:00:00Z',
    updatedAt: '2025-11-15T00:00:00Z',
  },
];

// Main tasks from the CSV data
export const mavenPromoTasks: Task[] = [
  // Checkout Epic Tasks
  {
    id: 'MP-F3-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Design & implement checkout workflow',
    description: 'Complete checkout workflow from add to cart to order confirmation including preview',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-checkout',
    estimatedDays: 14,
    estimatedHours: 112,
    llmAdjustedHours: 80,
    dependencies: '',
    skillsRequired: 'React, Next.js, TypeScript, Payment APIs',
    phase: '1',
    deliverables: 'Complete checkout flow',
    notes: 'Maven Release 1 - From Maven',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-19T00:00:00Z',
    updatedAt: '2025-09-15T00:00:00Z',
    startDate: '2025-08-19T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [{ type: 'Reference', url: 'https://mavenpromo.com/checkout' }],
    changeLogs: [],
  },
  {
    id: 'MP-CART-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Cloud Functions for checkout management',
    description: 'Backend cloud functions to handle checkout process, validation, and order creation',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-checkout',
    estimatedDays: 8,
    estimatedHours: 64,
    llmAdjustedHours: 64,
    dependencies: '',
    skillsRequired: 'Firebase Cloud Functions, TypeScript',
    phase: '1',
    deliverables: 'Checkout cloud functions',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-26T00:00:00Z',
    updatedAt: '2025-09-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-CART-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Cloud Tasks/Functions for order life-cycle management',
    description: 'Implement cloud functions for managing order lifecycle including status updates and notifications',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-order-tracking',
    estimatedDays: 8,
    estimatedHours: 64,
    llmAdjustedHours: 64,
    dependencies: 'MP-CART-001',
    skillsRequired: 'Firebase Cloud Functions, Cloud Tasks',
    phase: '1',
    deliverables: 'Order lifecycle management system',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-01T00:00:00Z',
    updatedAt: '2025-09-25T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-CART-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Add Floating Cart',
    description: 'Floating cart component that persists across pages and shows item count',
    type: 'Feature',
    status: 'Completed',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-checkout',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 12,
    dependencies: '',
    skillsRequired: 'React, CSS',
    phase: '1',
    deliverables: 'Floating cart UI component',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-10T00:00:00Z',
    updatedAt: '2025-09-12T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Admin Epic Tasks
  {
    id: 'MP-ADMIN-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Admin utility to change front page items',
    description: 'Admin panel interface to configure homepage featured products and banners',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: ['user-rafi'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 28,
    dependencies: '',
    skillsRequired: 'React, Firebase',
    phase: '1',
    deliverables: 'Homepage configuration panel',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-20T00:00:00Z',
    updatedAt: '2025-09-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [{ type: 'Admin', url: 'https://admin.mavenpromo.com' }],
    changeLogs: [],
  },
  {
    id: 'MP-ADMIN-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Category management UI',
    description: 'Admin interface for creating and managing product categories and subcategories',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 36,
    dependencies: '',
    skillsRequired: 'React, TypeScript',
    phase: '1',
    deliverables: 'Category management interface',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-22T00:00:00Z',
    updatedAt: '2025-09-08T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ADMIN-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Category management service',
    description: 'Backend service layer for category CRUD operations',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Low',
    category: 'Backend',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 20,
    dependencies: '',
    skillsRequired: 'Firebase, TypeScript',
    phase: '1',
    deliverables: 'Category service layer',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-22T00:00:00Z',
    updatedAt: '2025-09-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ADMIN-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Clipart management UI',
    description: 'Admin interface for uploading and managing clipart library for customization',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 28,
    dependencies: '',
    skillsRequired: 'React, File Upload',
    phase: '1',
    deliverables: 'Clipart management interface',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-25T00:00:00Z',
    updatedAt: '2025-09-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ADMIN-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Clipart management service',
    description: 'Backend service for clipart storage, retrieval and categorization',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 20,
    dependencies: '',
    skillsRequired: 'Firebase Storage',
    phase: '1',
    deliverables: 'Clipart service layer',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-25T00:00:00Z',
    updatedAt: '2025-09-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ADMIN-006',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Product Service-CRUD',
    description: 'Backend service for product creation, read, update, delete operations',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-product',
    estimatedDays: 6,
    estimatedHours: 48,
    llmAdjustedHours: 40,
    dependencies: '',
    skillsRequired: 'Firebase, TypeScript',
    phase: '1',
    deliverables: 'Product CRUD service',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-01T00:00:00Z',
    updatedAt: '2025-09-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ADMIN-007',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Add product UI',
    description: 'Admin interface for adding new products with all configurations',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['user-rafi'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 6,
    estimatedHours: 48,
    llmAdjustedHours: 42,
    dependencies: 'MP-ADMIN-006',
    skillsRequired: 'React, Forms',
    phase: '1',
    deliverables: 'Product creation form',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-05T00:00:00Z',
    updatedAt: '2025-09-25T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ADMIN-008',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Edit product, Products list UI',
    description: 'Admin interface for editing existing products and viewing product list',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['user-rafi'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 36,
    dependencies: 'MP-ADMIN-007',
    skillsRequired: 'React, Tables',
    phase: '1',
    deliverables: 'Product edit and list views',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-10T00:00:00Z',
    updatedAt: '2025-09-28T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ADMIN-009',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'General Settings Configuration Panel',
    description: 'Admin panel for configuring site-wide settings including shipping, taxes, and general options',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 6,
    estimatedHours: 48,
    llmAdjustedHours: 48,
    dependencies: '',
    skillsRequired: 'React, Forms',
    phase: '2',
    deliverables: 'Settings configuration interface',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-10-15T00:00:00Z',
    updatedAt: '2025-11-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Product Epic Tasks
  {
    id: 'MP-PRODUCT-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Product Service-Query',
    description: 'Backend service for product queries, filtering, and search',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-product',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 36,
    dependencies: 'MP-ADMIN-006',
    skillsRequired: 'Firebase Queries',
    phase: '1',
    deliverables: 'Product query service',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-15T00:00:00Z',
    updatedAt: '2025-10-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-PRODUCT-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Product Detail page',
    description: 'Customer-facing product detail page with images, options, and customization entry point',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-product',
    estimatedDays: 7,
    estimatedHours: 56,
    llmAdjustedHours: 50,
    dependencies: 'MP-PRODUCT-001',
    skillsRequired: 'React, Product Display',
    phase: '1',
    deliverables: 'Product detail page',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-20T00:00:00Z',
    updatedAt: '2025-10-15T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [{ type: 'Example', url: 'https://mavenpromo.com/products/3wkeblCwchTiuibYeSW9' }],
    changeLogs: [],
  },
  {
    id: 'MP-PRODUCT-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Price Engine - Product Price configurator & calculator',
    description: 'Pricing engine for calculating product prices based on options, quantity, and customization',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-product',
    estimatedDays: 8,
    estimatedHours: 64,
    llmAdjustedHours: 56,
    dependencies: '',
    skillsRequired: 'Business Logic, Algorithms',
    phase: '1',
    deliverables: 'Pricing calculation engine',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-26T00:00:00Z',
    updatedAt: '2025-09-20T00:00:00Z',
    startDate: '2025-08-26T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-PRODUCT-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Price metrics in product page',
    description: 'Display price breakdown and quantity-based pricing on product page',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-product',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 20,
    dependencies: 'MP-PRODUCT-003',
    skillsRequired: 'React, UI/UX',
    phase: '1',
    deliverables: 'Price display component',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-25T00:00:00Z',
    updatedAt: '2025-10-05T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Order Tracking Epic Tasks
  {
    id: 'MP-ORDER-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'My Orders Page with Edit Status Filter',
    description: 'Customer order history page with filtering by status',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-order-tracking',
    estimatedDays: 6,
    estimatedHours: 48,
    llmAdjustedHours: 48,
    dependencies: '',
    skillsRequired: 'React, Firebase Auth',
    phase: '1',
    deliverables: 'Order history page',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-10-10T00:00:00Z',
    updatedAt: '2025-10-25T00:00:00Z',
    startDate: '2025-10-10T00:00:00Z',
    targetEndDate: '2025-10-10T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ORDER-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Order List View with Status Filters',
    description: 'Admin view of all orders with status filters and bulk actions',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-order-tracking',
    estimatedDays: 6,
    estimatedHours: 48,
    llmAdjustedHours: 48,
    dependencies: '',
    skillsRequired: 'React, Data Tables',
    phase: '1',
    deliverables: 'Admin order list',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-10-15T00:00:00Z',
    updatedAt: '2025-11-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ORDER-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Order Search Functionality',
    description: 'Search orders by order ID, customer name, email, or product',
    type: 'Feature',
    status: 'Completed',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-order-tracking',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 40,
    dependencies: 'MP-ORDER-002',
    skillsRequired: 'Search, Indexing',
    phase: '1',
    deliverables: 'Order search feature',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-11-01T00:00:00Z',
    updatedAt: '2025-11-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ORDER-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Order Timeline Component',
    description: 'Visual timeline showing order progress from creation to delivery',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-order-tracking',
    estimatedDays: 8,
    estimatedHours: 64,
    llmAdjustedHours: 64,
    dependencies: 'MP-ORDER-001',
    skillsRequired: 'React, Timeline UI',
    phase: '1',
    deliverables: 'Order timeline component',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-11-05T00:00:00Z',
    updatedAt: '2025-11-25T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ORDER-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Summarization of the ordered product',
    description: 'Generate order summary from product schema and customer choices',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-order-tracking',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 28,
    dependencies: '',
    skillsRequired: 'Data Processing',
    phase: '1',
    deliverables: 'Order summarization logic',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-10-10T00:00:00Z',
    updatedAt: '2025-10-18T00:00:00Z',
    startDate: '2025-10-10T00:00:00Z',
    targetEndDate: '2025-10-12T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ORDER-006',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Mail Order Details after Checkout',
    description: 'Send order confirmation email with order details to customer',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-order-tracking',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 20,
    dependencies: 'MP-CART-001',
    skillsRequired: 'Email APIs, Templates',
    phase: '1',
    deliverables: 'Order confirmation email system',
    notes: 'Using Resend',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-10-13T00:00:00Z',
    updatedAt: '2025-10-20T00:00:00Z',
    startDate: '2025-10-13T00:00:00Z',
    targetEndDate: '2025-10-13T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-ORDER-007',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Order Type Management, Courier Management',
    description: 'Admin interface for managing order types and courier/shipping providers',
    type: 'Feature',
    status: 'In Progress',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-order-tracking',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 36,
    dependencies: '',
    skillsRequired: 'React, Admin UI',
    phase: '2',
    deliverables: 'Order type and courier management',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-02-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Customization Studio Tasks
  {
    id: 'MP-CUSTOM-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Customization Studio 2D (Full)',
    description: 'Complete 2D customization studio with text, clipart, shapes, layers, and export functionality',
    type: 'Feature',
    status: 'In Progress',
    priority: 'Critical',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['user-jakaria'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customize',
    estimatedDays: 40,
    estimatedHours: 320,
    llmAdjustedHours: 320,
    dependencies: '',
    skillsRequired: 'Canvas API, React, Graphics',
    phase: '1',
    deliverables: 'Complete 2D customization studio',
    notes: 'From Maven - Core feature',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-01T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-CUSTOM-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Data model for Customization Zones',
    description: 'Define data model for customization zones including dimensions, safe areas, and max DPI with admin UI',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['user-jakaria'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customize',
    estimatedDays: 6,
    estimatedHours: 48,
    llmAdjustedHours: 48,
    dependencies: '',
    skillsRequired: 'Data Modeling, Admin UI',
    phase: '1',
    deliverables: 'Customization zone data model and admin configuration',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-05T00:00:00Z',
    updatedAt: '2025-09-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-CUSTOM-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Service layer to retrieve, save or update user customization',
    description: 'Backend service for saving and retrieving design metadata and files',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customize',
    estimatedDays: 8,
    estimatedHours: 64,
    llmAdjustedHours: 64,
    dependencies: '',
    skillsRequired: 'Firebase, File Storage',
    phase: '1',
    deliverables: 'Customization persistence service',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-10T00:00:00Z',
    updatedAt: '2025-10-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Infrastructure Tasks
  {
    id: 'MP-INFRA-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Project setup [follow devops recommendation]',
    description: 'Initial project setup with Next.js, Firebase, and development environment',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: ['user-sakib'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-infrastructure',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 28,
    dependencies: '',
    skillsRequired: 'DevOps, Setup',
    phase: '1',
    deliverables: 'Project repository and setup',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-01T00:00:00Z',
    updatedAt: '2025-08-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-INFRA-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Initialize Firebase project and enable services',
    description: 'Setup Firebase project with Auth, Firestore, Storage, and Cloud Functions',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: ['user-sakib'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-infrastructure',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 32,
    dependencies: 'MP-INFRA-001',
    skillsRequired: 'Firebase',
    phase: '1',
    deliverables: 'Firebase project configuration',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-05T00:00:00Z',
    updatedAt: '2025-08-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-INFRA-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Configure Firebase Authentication providers',
    description: 'Setup email/password and Google authentication',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Low',
    category: 'Infrastructure',
    assigneeIds: ['user-sakib'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-infrastructure',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 32,
    dependencies: 'MP-INFRA-002',
    skillsRequired: 'Firebase Auth',
    phase: '1',
    deliverables: 'Auth configuration',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-08T00:00:00Z',
    updatedAt: '2025-08-18T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-INFRA-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Define Firestore schema and security rules',
    description: 'Design Firestore collections for products, orders, users and write security rules',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: ['hossainzarif2002@gmail.com', 'user-rafi', 'user-emtiaj', 'user-jakaria'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-infrastructure',
    estimatedDays: 8,
    estimatedHours: 64,
    llmAdjustedHours: 64,
    dependencies: 'MP-INFRA-002',
    skillsRequired: 'Firestore, Security',
    phase: '1',
    deliverables: 'Firestore schema and rules',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-10T00:00:00Z',
    updatedAt: '2025-08-25T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-INFRA-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Initialize Cloud Functions directory',
    description: 'Setup cloud functions project structure and configuration',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Low',
    category: 'Infrastructure',
    assigneeIds: ['user-sakib'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-infrastructure',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 16,
    dependencies: 'MP-INFRA-002',
    skillsRequired: 'Cloud Functions',
    phase: '1',
    deliverables: 'Cloud functions setup',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-12T00:00:00Z',
    updatedAt: '2025-08-16T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-INFRA-006',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Deployment at firebase app hosting with github actions',
    description: 'Setup CI/CD pipeline for automatic deployment to Firebase',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-infrastructure',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 36,
    dependencies: 'MP-INFRA-001',
    skillsRequired: 'GitHub Actions, CI/CD',
    phase: '1',
    deliverables: 'Deployment pipeline',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-20T00:00:00Z',
    updatedAt: '2025-09-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-INFRA-007',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Set up Firebase Storage bucket for product images',
    description: 'Configure storage bucket with proper permissions and CORS',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Low',
    category: 'Infrastructure',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-infrastructure',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 16,
    dependencies: 'MP-INFRA-002',
    skillsRequired: 'Firebase Storage',
    phase: '1',
    deliverables: 'Storage bucket configuration',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-15T00:00:00Z',
    updatedAt: '2025-08-22T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Browse/Navigation Tasks
  {
    id: 'MP-BROWSE-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Homepage frontend redesign',
    description: 'Redesign homepage with hero section, featured products, and categories',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['user-adil'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-browse',
    estimatedDays: 8,
    estimatedHours: 64,
    llmAdjustedHours: 56,
    dependencies: '',
    skillsRequired: 'React, UI/UX Design',
    phase: '1',
    deliverables: 'Redesigned homepage',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-15T00:00:00Z',
    updatedAt: '2025-10-10T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [{ type: 'Live', url: 'https://mavenpromo.com' }],
    changeLogs: [],
  },
  {
    id: 'MP-BROWSE-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Fix Search Section',
    description: 'Improve search functionality with better filtering and results display',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['user-jakaria'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-browse',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 32,
    dependencies: '',
    skillsRequired: 'Search, UI',
    phase: '2',
    deliverables: 'Improved search',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-10T00:00:00Z',
    updatedAt: '2026-01-10T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Payment Tasks
  {
    id: 'MP-PAYMENT-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Front-end utility for payment processing',
    description: 'Frontend payment processing integration with Stripe',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-payment',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 32,
    dependencies: '',
    skillsRequired: 'Stripe API, React',
    phase: '1',
    deliverables: 'Payment UI integration',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-28T00:00:00Z',
    updatedAt: '2025-09-10T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'MP-PAYMENT-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Configure Stripe API keys in Cloud Functions',
    description: 'Add Stripe secret and publishable keys to cloud functions environment',
    type: 'Feature',
    status: 'In Progress',
    priority: 'Critical',
    complexity: 'Low',
    category: 'Infrastructure',
    assigneeIds: ['user-sakib'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-payment',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 16,
    dependencies: 'MP-INFRA-005',
    skillsRequired: 'Firebase, Configuration',
    phase: '1',
    deliverables: 'Stripe configuration',
    notes: 'Maven Release 1',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-01T00:00:00Z',
    updatedAt: '2026-02-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
];

// Sprint tasks from the bug tracking table
export const mavenPromoSprintTasks: Task[] = [
  {
    id: 'MP-BUG-FEB7',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Lanyard - Wrist lanyard doesn\'t show customization',
    description: 'Wrist lanyard product not displaying customization preview correctly. URL: https://mavenpromo.com/products/3wkeblCwchTiuibYeSW9',
    type: 'Bug',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['user-jakaria'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customize',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'Customization, Debugging',
    phase: 'Sprint',
    deliverables: 'Fixed lanyard customization display',
    notes: 'Reported by Masud on Feb 7 2026, Verified by Adil',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-07T00:00:00Z',
    updatedAt: '2026-02-10T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [{ type: 'Product', url: 'https://mavenpromo.com/products/3wkeblCwchTiuibYeSW9' }],
    changeLogs: [],
    reportedBy: 'user-masud',
  },
  {
    id: 'MP-BUG-FEB5',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Cap - Black cap mockup issues',
    description: 'Black cap has multiple issues: only one mockup shown (should match thumbnails), black mask appeared with mockup, product creation steps not followed properly causing issues',
    type: 'Bug',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Product Management',
    assigneeIds: ['user-saif'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-product',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 22,
    dependencies: '',
    skillsRequired: 'Product Configuration, Mockups',
    phase: 'Sprint',
    deliverables: 'Fixed cap mockup display, create FAQ for product setup',
    notes: 'Reported by Asif on Feb 6 2026, Verified by Tias and Asif. Need to create FAQ explaining proper product setup',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-06T00:00:00Z',
    updatedAt: '2026-02-12T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: 'asif.redmond@gmail.com',
  },
  {
    id: 'MP-BUG-FEB8',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Categories - Multiple catalog popup issues',
    description: 'Multiple issues: Catalog popup doesn\'t appear on categories page, some categories missing images, some subcategories show up but have no products, category images wait for image url in infinite loop',
    type: 'Bug',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['user-jakaria'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-browse',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 20,
    dependencies: '',
    skillsRequired: 'React, Category Management',
    phase: 'Sprint',
    deliverables: 'Fixed catalog popup and category display issues',
    notes: 'Reported by Masud on Feb 8 2026, Verified by Masud. Catalog popup was disabled by Zarif for previous case',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-08T00:00:00Z',
    updatedAt: '2026-02-14T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: 'user-masud',
  },
  {
    id: 'MP-BUG-FEB21',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Incorrect color shown in the cart',
    description: 'Selected product color is not displayed correctly in the shopping cart',
    type: 'Bug',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['user-adil'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-checkout',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'Cart Logic, State Management',
    phase: 'Sprint',
    deliverables: 'Fixed color display in cart',
    notes: 'Reported by Masud on Feb 21 2026, Verified by Masud',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-21T00:00:00Z',
    updatedAt: '2026-02-22T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: 'user-masud',
  },
  {
    id: 'MP-FEAT-CLIPCOLOR',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Allow user to change clipart color in product page',
    description: 'Implement clipart color changing functionality in the customization studio. Add color swapping and predefined color change options similar to Printful',
    type: 'Feature',
    status: 'In Progress',
    priority: 'High',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['user-adil'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customize',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 38,
    dependencies: '',
    skillsRequired: 'Canvas API, Color Manipulation',
    phase: 'Sprint',
    deliverables: 'Clipart color change feature with predefined and custom options',
    notes: 'Reported by Mahi on Feb 4 2026. Color change issue - not changing when clicking different colors. Need color swap functionality and Printful-style predefined color changes',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-04T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: 'user-mahi',
  },
  {
    id: 'MP-FEAT-COLORFEB6',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Selected color not shown in cart as text',
    description: 'Add color selection option in product page and show selected color in cart. If colored image present, Design Studio should load corresponding image. If color mask set, apply color transformation. Reflect color changes in product page when returning from Design Studio',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['user-tias'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-product',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 30,
    dependencies: '',
    skillsRequired: 'Product Page, Cart, State Management',
    phase: 'Sprint',
    deliverables: 'Color selection in product page and cart display',
    notes: 'Reported by Mahi on Feb 4 2026, Verified by Tias. Includes navigation flow and Design Studio integration',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-04T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: 'user-mahi',
  },
  {
    id: 'MP-FEAT-SHIPPING',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Shipping options - Price adjustment cannot be increased over 1',
    description: 'Fix bug in shipping options where price adjustment value is limited to 1',
    type: 'Bug',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Low',
    category: 'Backend',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 1,
    estimatedHours: 8,
    llmAdjustedHours: 6,
    dependencies: '',
    skillsRequired: 'Backend, Validation',
    phase: 'Sprint',
    deliverables: 'Fixed shipping price adjustment limits',
    notes: 'Reported by Mahi on Feb 4 2026, Verified by Tias. Changed to Emtiaj on Feb 6',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-04T00:00:00Z',
    updatedAt: '2026-02-07T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: 'user-mahi',
  },
  {
    id: 'MP-FEAT-CURVED-MASK',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Curved path feature in creating mask',
    description: 'Add curved path capability to mask creation tool for products with curved customization areas',
    type: 'Feature',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['user-saif'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 38,
    dependencies: '',
    skillsRequired: 'Canvas API, SVG Paths',
    phase: 'Sprint',
    deliverables: 'Curved path mask creation tool',
    notes: 'Reported by Mahi on Feb 4 2026, Verified by Tias',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-04T00:00:00Z',
    updatedAt: '2026-02-18T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: 'user-mahi',
  },
  {
    id: 'MP-IMPROVE-MARKDOWN',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Product description markdown editor in preview mode',
    description: 'Add preview mode to markdown editor for product descriptions',
    type: 'Improvement',
    status: 'Completed',
    priority: 'Low',
    complexity: 'Low',
    category: 'Frontend',
    assigneeIds: ['user-jakaria'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'Markdown, React',
    phase: 'Sprint',
    deliverables: 'Markdown preview mode',
    notes: 'Reported by Mahi on Feb 4 2026, Verified by Tias',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-04T00:00:00Z',
    updatedAt: '2026-02-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: 'user-mahi',
  },
  {
    id: 'MP-FEAT-CART-QUANTITY',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Allow increase/decrease product count in cart',
    description: 'Add quantity adjustment controls in cart with automatic price and shipping recalculation',
    type: 'Feature',
    status: 'Completed',
    priority: 'Low',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: ['user-emtiaj'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-checkout',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 22,
    dependencies: '',
    skillsRequired: 'Cart Logic, Price Calculation',
    phase: 'Sprint',
    deliverables: 'Cart quantity adjustment feature',
    notes: 'Reported by Mahi on Feb 4 2026, Verified by Tias. Ensure shipping and price update according to rules',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-04T00:00:00Z',
    updatedAt: '2026-02-16T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: 'user-mahi',
  },
];

// Create sprint
export const mavenPromoSprint: Sprint = {
  id: 'sprint-mavenpromo-feb-2026',
  organizationId: ORG_ID,
  projectId: PROJECT_ID,
  name: 'MavenPromo - February 2026 Bug Fixes & Features',
  startDate: '2026-02-01',
  endDate: '2026-02-28',
  status: 'Active',
  taskIds: mavenPromoSprintTasks.map(t => t.id),
  goals: 'Address critical bugs affecting customer experience including lanyard customization, cart color display, category navigation issues. Implement color selection features and cart improvements.',
  createdAt: '2026-02-01T00:00:00Z',
  updatedAt: '2026-02-22T00:00:00Z',
};

// Standup Notes
export const mavenPromoStandupNotes: StandupNote[] = [
  {
    id: 'standup-mp-feb22-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `Meeting with Mahi vai regarding design studio
- Clip art color change instantaneous
- Color selection from outside without creating the design
- The price minus not working for the minus option but plus works
- UI bug fix
- Blank space on both sides in studio and proportion changes
- Pan tool order change so the color selection option is visible when the design studio is open
- Adil code review`,
    createdAt: '2026-02-22T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb19-zarif',
    organizationId: ORG_ID,
    authorId: 'hossainzarif2002@gmail.com',
    content: `Parsed document using LlamaIndex
- Removed page headers (Page metadata)
- Tagged equations, not tagged by the parser
- Split equations mixed with text
- Converted equations type from text to equation and stored the tag
- Identified lists misidentified by the parser and fixed them
- Merged continued lists
- Grouped caption, table and footnotes into a table group
- Fixed the bbox issues`,
    createdAt: '2026-02-19T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb19-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `Design and mockup creation video
https://www.youtube.com/watch?v=K_bEJ4H5eQU&feature=youtu.be`,
    createdAt: '2026-02-19T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb18-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `- Checking bug list given by mahi vai
- Design creation guidance tools added for easier design addition`,
    createdAt: '2026-02-18T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb17-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `Minor bugs related to mockup and design data finding and fix`,
    createdAt: '2026-02-17T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb15-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `Home page popup config, clarification`,
    createdAt: '2026-02-15T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb12-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `Advanced wrap tool implementation
- 3*3 cage wrapping tool that can take into account compression and stretch in texture and properly wraps around curved surfaces`,
    createdAt: '2026-02-12T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb11-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `Research on generalized texture wrap tool for a better mockup for the cases like cap and t shirt`,
    createdAt: '2026-02-11T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb10-adil',
    organizationId: ORG_ID,
    authorId: 'user-adil',
    content: `No update (was traveling for vote)`,
    createdAt: '2026-02-10T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb9-adil',
    organizationId: ORG_ID,
    authorId: 'user-adil',
    content: `Tried adding a mask editor but discarded since already present`,
    createdAt: '2026-02-09T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb8-adil',
    organizationId: ORG_ID,
    authorId: 'user-adil',
    content: `- Showing selected color in cart
- Color selection in product page (Admin side changes are in progress)`,
    createdAt: '2026-02-08T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb8-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `ColorPlacementImage manual upload`,
    createdAt: '2026-02-08T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb7-adil',
    organizationId: ORG_ID,
    authorId: 'user-adil',
    content: `Catalog Refined`,
    createdAt: '2026-02-07T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb5-zarif',
    organizationId: ORG_ID,
    authorId: 'hossainzarif2002@gmail.com',
    content: `- Meeting with Maven
- 4 Feb Maven Meeting
- Meeting with Rifat vai regarding building code automation
- Meeting with Apro regarding building code processing plan
- Zarif Apro Meeting 4 Feb - Finalizing Plan for Building Code Processing`,
    createdAt: '2026-02-05T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb5-emtiaj',
    organizationId: ORG_ID,
    authorId: 'user-emtiaj',
    content: `Raw design artifacts in production order`,
    createdAt: '2026-02-05T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb5-adil',
    organizationId: ORG_ID,
    authorId: 'user-adil',
    content: `- Column swap for price table
- Planning for CAD design to 3D`,
    createdAt: '2026-02-05T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb5-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `PSD, AI file format support for user image upload (partial test, there is a need for a separate backend server for the psd, ai to png format transformation)`,
    createdAt: '2026-02-05T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb4-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `Meeting
Check the design studio + mockup tutorial video that addresses every option and the full flow of product design creation
- (Done) Masking control line curve
- (Done) Anti aliasing while using mask
- (Done) Old image upload for design image for each color product, keep the option along with the automated design image generation`,
    createdAt: '2026-02-04T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb3-zarif',
    organizationId: ORG_ID,
    authorId: 'hossainzarif2002@gmail.com',
    content: `Going through Apro's code`,
    createdAt: '2026-02-03T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb3-emtiaj',
    organizationId: ORG_ID,
    authorId: 'user-emtiaj',
    content: `Category page fix: showed both sub category and products`,
    createdAt: '2026-02-03T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb3-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `Done:
- Thumbnail image dimension documentation
Working:
- Psd,svg,eps file support for the users in client side`,
    createdAt: '2026-02-03T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb3-rafi',
    organizationId: ORG_ID,
    authorId: 'user-rafi',
    content: `- Add product cleanup related bug fix
- Client side global error handler page`,
    createdAt: '2026-02-03T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb2-zarif',
    organizationId: ORG_ID,
    authorId: 'hossainzarif2002@gmail.com',
    content: `- Meeting with Mahi Vai
- 10 PM meeting with the building code compliance project CS team
- Did some manual testing on the design studio to find out bugs
- Plan for Building Code Compliance
- Understanding The Detailed Requirement Document
- Understanding the required sections of BNBC building code
- Finalizing a project requirement from our side
- Understanding a pipeline developed for BNBC code clause parsing`,
    createdAt: '2026-02-02T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb2-emtiaj',
    organizationId: ORG_ID,
    authorId: 'user-emtiaj',
    content: `Payment gateway project docs: Payment Gateway
Maven Admin operation log: https://admin.mavenpromo.com/admin-logs`,
    createdAt: '2026-02-02T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb2-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `Finding out missing data from product`,
    createdAt: '2026-02-02T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb1-zarif',
    organizationId: ORG_ID,
    authorId: 'hossainzarif2002@gmail.com',
    content: `Read papers and slides on Building Code Compliance Project`,
    createdAt: '2026-02-01T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb1-rafi',
    organizationId: ORG_ID,
    authorId: 'user-rafi',
    content: `- Fixed a bug related to product images in client side
- Fixed bug in edit product where if previously configured content dependency is changed to no dependency it was not being saved`,
    createdAt: '2026-02-01T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
  {
    id: 'standup-mp-feb1-saif',
    organizationId: ORG_ID,
    authorId: 'user-saif',
    content: `- Meeting with Mahi vai 
- Design color automation mask generation
- Smooth scale change of texture in cap`,
    createdAt: '2026-02-01T10:00:00Z',
    projectId: PROJECT_ID,
    sprintId: 'sprint-mavenpromo-feb-2026',
  },
];
